// const API_BASE = `http://localhost:8000/chats`;

import type { AxiosRequestConfig } from "axios";
import { API } from "@/constants";
import { get, post, del, put  } from "./api";
import { InvitedUsersParams } from "@/hooks/useRbac";
import { InviteUserPayload } from "@/hooks/useUserManagement";

/*
 * expected response for getAllRoles
 * need roleIds from response to fetch next set of permissions
{
    "data": [
        {
            "roleId": 15,
            "name": "OrgAdmin",
            "roleName": "Admin",
            "description": "Admin who can manage everything",
            "customRole": false,
            "productId": 2,
            "parentRoles": []
        },
        {
            "roleId": 16,
            "name": "AppAdmin",
            "roleName": "App Admin",
            "description": "User who can manage dashboards, sources, destinations",
            "customRole": false,
            "productId": 2,
            "parentRoles": []
        }
        ...
    ],
    "errors": []
}
*/
/**
 * ALWAYS opts out of the global error toast: every caller of this endpoint
 * (`useRbac`, `useShareRoles`) surfaces its own failure — `useRbac` through
 * `AuthContext.roleAccessError`, the share dialog by degrading to an empty picker.
 * Making the policy a property of the endpoint (not of the caller) is what lets
 * both share ONE `["roles"]` react-query cache entry: with a per-caller option, the
 * first caller's error policy would win for both, which previously forced a
 * policy-keyed cache fork and a duplicate GET per session.
 */
export const getAllRoles = async (config?: AxiosRequestConfig) => {
  const roles: any = await get(API.RBAC.role, { ...config, skipGlobalErrorHandler: true });
  const data = roles?.data || roles;
  if(Array.isArray(data)){
    return data
      .filter((role: any) => Number(role.productId) === 4)
  }
  return data;
};

/*
 * expected response for getRolePermissions
{
    "data": {
        "role": {
            "roleId": 15,
            "name": "OrgAdmin",
            "roleName": "Admin",
            "description": "Admin who can manage everything",
            "customRole": false,
            "productId": 2,
            "parentRoles": []
        },
        "permissions": [
            {
                "permissionId": 65,
                "name": "insights:user:user:read",
                "description": "View groups owned by user"
            },
            {
                "permissionId": 66,
                "name": "insights:user:user:write",
                "description": "Manage groups owned by user"
            }...
        ]
    },
    "errors": []
}
*/
/**
 * Opts out of the global error toast, mirroring `getAllRoles`: this runs once per
 * role during capability resolution, so a failure should degrade the gates closed
 * QUIETLY (the caller `useRbac` surfaces it via `AuthContext.roleAccessError`), not
 * spray one "Not Found" toast per role.
 */
export const getRolePermissions = async (roleId: number) => {
  const permissions: any = await get(API.RBAC.permissions(roleId), { skipGlobalErrorHandler: true });
  const data = permissions?.data || permissions;
  return data;
};

export const getInvitedUsers = async (params: InvitedUsersParams, config?: AxiosRequestConfig) => {
  const invitedUsers: any = await get(API.RBAC.invitedUsers(params), config);
  const data = invitedUsers?.data || invitedUsers;
  return data;
};

export const inviteUsers = async ({ companyId, payload }: { companyId: number, payload: InviteUserPayload[] }) => {
  return await post(API.RBAC.inviteUser(companyId), payload);
};

export const reinviteUser = async ({ companyId, userId }: { companyId: number, userId: number }) => {
  return await post(API.RBAC.reinviteUser(companyId, userId));
};

export const editInvitedUser = async ({ companyId, userId, payload }: { companyId: number, userId: number, payload: {roleIds: number[]} }) => {
  return await put(API.RBAC.editUser(companyId, userId), payload);
};

export const deleteInvitedUser = async ({ companyId, userId }: { companyId: number, userId: number }) => {
  return await del(API.RBAC.deleteUser(companyId, userId));
};

/*
let d1 = {
    "data": [
        {
            "roleId": 15,
            "name": "IQUser",
            "roleName": "Admin",
            "description": "Admin who can manage everything",
            "customRole": false,
            "productId": 3,
            "parentRoles": []
        },
        {
            "roleId": 16,
            "name": "IQAdmin",
            "roleName": "App Admin",
            "description": "User who can manage dashboards, sources, destinations",
            "customRole": false,
            "productId": 3,
            "parentRoles": []
        }
    ],
    "errors": []
}
let d2: any = {
    '15': {
        "data": {
            "role": {
                "roleId": 15,
                "name": "IQAdmin",
                "roleName": "Admin",
                "description": "Admin who can manage everything",
                "customRole": false,
                "productId": 2,
                "parentRoles": []
            },
            "permissions": [
                {
                    "permissionId": 65,
                    "name": "insights:user:user:read",
                    "description": "View groups owned by user"
                },
                {
                    "permissionId": 66,
                    "name": "insights:user:user:write",
                    "description": "Manage groups owned by user"
                }
            ]
        },
        "errors": []
    },
    '16': {
        "data": {
            "role": {
                "roleId": 16,
                "name": "IQUser",
                "roleName": "Admin",
                "description": "Admin who can manage everything",
                "customRole": false,
                "productId": 2,
                "parentRoles": []
            },
            "permissions": [
                {
                    "permissionId": 65,
                    "name": "insights:user:user:read",
                    "description": "View groups owned by user"
                },
                {
                    "permissionId": 66,
                    "name": "insights:user:user:write",
                    "description": "Manage groups owned by user"
                }
            ]
        },
        "errors": []
    },
}*/